"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { Home, LogIn, HelpCircle } from "lucide-react";

export default function NotFound() {
  const glitchRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = glitchRef.current;
    if (!el) return;
    const interval = setInterval(() => {
      el.setAttribute("data-glitch", "true");
      setTimeout(() => el.removeAttribute("data-glitch"), 220);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="h-screen overflow-hidden bg-background text-foreground flex flex-col">
      {/* Nav */}


      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center text-center px-6 overflow-hidden">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-primary mb-6">
          <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          404 — Page not found
        </div>

        {/* Giant 404 */}
        <div className="relative mb-2 select-none" aria-hidden>
          <span
            ref={glitchRef}
            className="nf-404 block font-extrabold leading-none tracking-tighter"
          >
            404
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
          Lost in the <span className="text-primary">void</span>
        </h1>

        <p className="text-muted-foreground text-sm leading-relaxed max-w-md mb-7">
          The page you&apos;re looking for doesn&apos;t exist, was moved, or
          the link may be broken. Let&apos;s get you back on track.
        </p>

        {/* CTA buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:opacity-90 hover:-translate-y-px hover:shadow-lg"
          >
            <Home className="h-3.5 w-3.5" />
            Go home
          </Link>
          <Link
            href="/login"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-2.5 text-sm font-semibold text-foreground transition-all hover:bg-muted"
          >
            <LogIn className="h-3.5 w-3.5" />
            Sign in
          </Link>
        </div>

        {/* Suggestions */}
        <div className="w-full max-w-2xl">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
            You might be looking for
          </p>
          <div className="grid grid-cols-3 gap-3">
            {[
              { href: "/login",  icon: LogIn,      title: "Sign in",        desc: "Access your dashboard" },
              { href: "/signup", icon: Home,        title: "Create account", desc: "Get started for free"  },
              { href: "/help",   icon: HelpCircle,  title: "Help center",    desc: "Find answers & guides" },
            ].map(({ href, icon: Icon, title, desc }) => (
              <Link
                key={href}
                href={href}
                className="group flex flex-col items-center text-center gap-2 rounded-xl border border-border bg-card px-4 py-5 transition-all hover:border-primary/30 hover:bg-primary/5 hover:-translate-y-0.5"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground whitespace-nowrap">{title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 whitespace-nowrap">{desc}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>

      <style jsx global>{`
        .nf-404 {
          font-size: clamp(4rem, 15vw, 10rem);
          color: transparent;
          -webkit-text-stroke: 1.5px color-mix(in oklch, var(--border) 80%, transparent);
          position: relative;
        }

        .nf-404::before,
        .nf-404::after {
          content: "404";
          position: absolute;
          inset: 0;
          color: transparent;
        }

        .nf-404[data-glitch="true"]::before {
          -webkit-text-stroke: 1.5px var(--primary);
          clip-path: polygon(0 15%, 100% 15%, 100% 38%, 0 38%);
          transform: translateX(-5px);
          animation: nfG1 0.18s steps(2) forwards;
        }

        .nf-404[data-glitch="true"]::after {
          -webkit-text-stroke: 1.5px color-mix(in oklch, var(--primary) 50%, var(--destructive));
          clip-path: polygon(0 62%, 100% 62%, 100% 82%, 0 82%);
          transform: translateX(5px);
          animation: nfG2 0.18s steps(2) forwards;
        }

        @keyframes nfG1 {
          0%   { transform: translateX(-5px); }
          50%  { transform: translateX(4px); }
          100% { transform: translateX(-5px); }
        }

        @keyframes nfG2 {
          0%   { transform: translateX(5px); }
          50%  { transform: translateX(-4px); }
          100% { transform: translateX(5px); }
        }
      `}</style>
    </div>
  );
}
